/**
 * Portal Manta · RelatoriosPage
 */
import * as XLSX from 'xlsx';
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { KPIS_QUALI_SCHEMA, KPIS_QUANTI_SCHEMA } from '../../data/kpis-schemas.js';
import { M, F, STATUS } from '../../tokens/theme.js';
import { cossenoVec } from '../../utils/cosseno.js';
import { downloadBlob, workbookToBlob } from '../../utils/xlsx-helpers.js';
import { wrapHtmlManta } from '../../utils/html-wrap.js';
import { S, extKm } from '../../globals.js';
import { htmlTabelaKPIs } from '../agentes/htmlTabelaKPIs.jsx';
import { Fonte } from '../shared/ArtefatoHelpers.jsx';
import { abrirParaImprimir } from '../agentes/abrirParaImprimir.jsx';
import { LinhaRelatorio } from './LinhaRelatorio.jsx';

const RelatoriosPage = memo(function RelatoriosPage_Inner({
  qual, kpis, fontes, orcamento, loteRef, similaridades, casosFiltrados, salvosHist, baseLotes,
}){
  const [msgUltimo, setMsgUltimo] = useState('');
  const [preview, setPreview] = useState(null); // { id, titulo, html, fmtOriginal, fnDownload }
  const flash = (m) => { setMsgUltimo(m); setTimeout(()=>setMsgUltimo(''), 4000); };
  const nomeEstudo = (qual.nome || qual.codManta || 'estudo-sem-nome').replace(/[^a-z0-9-]+/gi, '_').slice(0, 60);
  const dataHoje = new Date().toISOString().slice(0, 10);

  // Abrir preview lateral com o HTML do relatório · v28.9
  const abrirPreview = (rel) => {
    try {
      const html = rel.htmlFn();
      setPreview({
        id: rel.id, titulo: rel.titulo, html,
        fmtOriginal: rel.fmt, fnDownload: rel.fn,
        disabled: rel.disabled,
      });
    } catch(e) {
      alert(`Erro ao gerar preview: ${e.message}`);
    }
  };

  // Exportar HTML do preview atual
  const exportarHtmlPreview = () => {
    if (!preview) return;
    const blob = new Blob([preview.html], {type:'text/html'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Manta_${nomeEstudo}_${dataHoje}_${preview.id}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    flash(`${preview.id} · HTML baixado`);
  };

  // ═══════════════════════════════════════════════════════════════════
  // GRUPO A · RELATÓRIOS POR ESTUDO (A1-A5)
  // ═══════════════════════════════════════════════════════════════════

  // ── A1 · Ficha Técnica do Estudo (PDF via print) ────────────────────
  const gerarA1 = () => {
    const tabQuali = htmlTabelaKPIs('KPIs Qualitativos · 22 campos', KPIS_QUALI_SCHEMA, qual, fontes);
    const tabQuanti = htmlTabelaKPIs('KPIs Quantitativos · 18 campos', KPIS_QUANTI_SCHEMA, kpis, fontes);

    // Resumo de fontes
    const fonteCount = {};
    Object.values(fontes).forEach(f => { fonteCount[f] = (fonteCount[f]||0) + 1; });

    const corpo = `
      <h2>1 · Identificação do Estudo</h2>
      <table>
        <tr><th style="width:30%">Campo</th><th>Valor</th></tr>
        <tr><td>Nome</td><td>${qual.nome || '—'}</td></tr>
        <tr><td>Código Manta</td><td>${qual.codManta || '—'}</td></tr>
        <tr><td>UF Principal</td><td>${qual.ufPrincipal || qual.uf || '—'}</td></tr>
        <tr><td>Cidade</td><td>${qual.cidade || '—'}</td></tr>
        <tr><td>Tipo de Obra</td><td>${qual.tipoObra || '—'}</td></tr>
        <tr><td>Concessionária</td><td>${qual.concessionaria || '—'}</td></tr>
        <tr><td>Regulador</td><td>${qual.regulador || '—'}</td></tr>
        <tr><td>Prazo de Concessão</td><td>${qual.prazoAnos || '—'} anos</td></tr>
        <tr><td>Data do Estudo</td><td>${qual.dataEstudo || '—'}</td></tr>
        <tr><td>Nível de Detalhe</td><td>${qual.nivelDetalhe || '—'}</td></tr>
      </table>

      <h2>2 · Indicadores Físicos Principais</h2>
      <div>
        <div class="kpi-card"><div class="l">Extensão Total</div><div class="v">${kpis.extKm || 0}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">Duplicação</div><div class="v">${kpis.dupKm || 0}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">OAEs</div><div class="v">${kpis.oaeQt || 0}<span class="u">un</span></div></div>
        <div class="kpi-card"><div class="l">Interseções</div><div class="v">${kpis.interQt || 0}<span class="u">un</span></div></div>
        <div class="kpi-card"><div class="l">Passarelas</div><div class="v">${kpis.passQt || 0}<span class="u">un</span></div></div>
        <div class="kpi-card"><div class="l">Acessos</div><div class="v">${kpis.acessosQt || 0}<span class="u">un</span></div></div>
      </div>

      ${tabQuali}
      ${tabQuanti}

      <h2>3 · Auditoria de Fontes (Cap 16)</h2>
      <table>
        <tr><th>Tipo de Fonte</th><th>Quantidade</th></tr>
        <tr><td>🟢 Extraído (PER, MEF, SharePoint)</td><td>${fonteCount.extraido || 0}</td></tr>
        <tr><td>🟡 Preset (template aplicado)</td><td>${fonteCount.preset || 0}</td></tr>
        <tr><td>🔵 Import (PDF/JSON/Excel)</td><td>${fonteCount.import || 0}</td></tr>
        <tr><td>⚪ Manual (digitado)</td><td>${fonteCount.manual || 0}</td></tr>
        <tr><td>— Vazio</td><td>${40 - Object.keys(fontes).length}</td></tr>
      </table>
    `;
    const html = wrapHtmlManta(`Ficha Técnica · ${qual.nome || qual.codManta || 'Estudo'}`, corpo, {
      meta: `${kpis.extKm} km · ${qual.ufPrincipal}`
    });
    abrirParaImprimir(html, 'Ficha Técnica');
    flash('A1 · Ficha Técnica aberta para impressão (Ctrl+P → Salvar como PDF)');
  };

  // ── A2 · Resumo Executivo (PDF 1 pg) ────────────────────────────────
  const gerarA2 = () => {
    const top3 = similaridades.slice(0, 3);
    const orcStr = orcamento ? `R$ ${(orcamento.med/1e6).toFixed(0)} milhões` : 'pendente · escolha caso de referência';

    const corpo = `
      <h2>Síntese</h2>
      <p style="margin:8px 0;font-size:12px;line-height:1.5">
        <b>${qual.nome || qual.codManta}</b> é um(a) <b>${qual.tipoObra || 'projeto'}</b>
        ${qual.ufPrincipal ? 'no estado <b>' + qual.ufPrincipal + '</b>' : ''}
        com extensão total de <b>${kpis.extKm || 0} km</b>, sendo <b>${kpis.dupKm || 0} km</b> de duplicação prevista,
        <b>${kpis.oaeQt || 0}</b> OAEs e <b>${kpis.interQt || 0}</b> interseções.
        ${qual.concessionaria ? 'Concessionária: <b>' + qual.concessionaria + '</b>.' : ''}
        ${qual.prazoAnos ? 'Prazo de concessão: <b>' + qual.prazoAnos + ' anos</b>.' : ''}
      </p>

      <h2>Estimativa de Orçamento</h2>
      <p style="margin:8px 0;font-size:14px"><b>${orcStr}</b></p>
      ${orcamento ? `<p style="font-size:10.5px;color:#6B7280">Δ comparativo entre Rota A (caso similar) e Rota B (média portfólio): ${orcamento.deltaAB.toFixed(1)}%</p>` : ''}

      <h2>Top 3 Casos Similares</h2>
      ${top3.length === 0 ? '<p style="color:#9CA3AF;font-style:italic">— Buscar similaridade primeiro</p>' : `
      <table>
        <tr><th>#</th><th>Lote</th><th>UF</th><th>Score</th><th>Drivers</th></tr>
        ${top3.map((s,i) => `<tr>
          <td>${i+1}</td>
          <td>${s.lote.sigla || s.lote.sig}</td>
          <td>${s.lote.uf || '—'}</td>
          <td><b>${s.score}%</b></td>
          <td style="font-size:10.5px">${(s.drivers||[]).join(' · ') || '—'}</td>
        </tr>`).join('')}
      </table>`}

      <h2>Próximos Passos</h2>
      <ol style="margin-left:20px;font-size:11.5px;line-height:1.6">
        <li>Validar quantitativos físicos pendentes (campos ainda vazios)</li>
        <li>Buscar similaridade ampliada com filtros de região e regime</li>
        <li>Refinar BDI específico da concessionária se aplicável</li>
        <li>Consolidar com retificação contratual se houver</li>
      </ol>
    `;
    const html = wrapHtmlManta(`Resumo Executivo · ${qual.nome || qual.codManta}`, corpo);
    abrirParaImprimir(html, 'Resumo Executivo');
    flash('A2 · Resumo Executivo aberto para impressão');
  };

  // ── A3 · Memória de Cálculo do Orçamento (Excel) ────────────────────
  const gerarA3 = () => {
    const wb = XLSX.utils.book_new();

    // Aba 1 · Identificação
    const aba1 = [
      ['MANTA ASSOCIADOS · Memória de Cálculo do Orçamento'],
      ['Estudo:', qual.nome || qual.codManta || ''],
      ['Data de geração:', new Date().toLocaleString('pt-BR')],
      [],
      ['Campo', 'Valor', 'Fonte'],
      ...Object.values(KPIS_QUALI_SCHEMA).flatMap(b => b.campos.map(c => [
        c.label, qual[c.k] ?? '', fontes[c.k] || ''
      ])),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba1), 'Identificação');

    // Aba 2 · KPIs Quantitativos
    const aba2 = [
      ['Bloco', 'Campo', 'Unidade', 'Valor', 'Fonte'],
      ...Object.entries(KPIS_QUANTI_SCHEMA).flatMap(([k,g]) => g.campos.map(c => [
        g.titulo.split('·')[0].trim(), c.label, c.sufixo || '', kpis[c.k] ?? 0, fontes[c.k] || ''
      ])),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba2), 'KPIs Quantitativos');

    // Aba 3 · Orçamento (se houver)
    if (orcamento && orcamento.rotaA) {
      const aba3 = [
        ['Categoria', 'Quantidade', 'Unid.', 'PU Rota A (R$)', 'Custo Rota A', 'PU Rota B (R$)', 'Custo Rota B'],
        ...orcamento.rotaA.map((r, i) => {
          const rb = orcamento.rotaB[i] || {};
          return [r.l, r.q, r.und, r.pu, r.custo, rb.pu || 0, rb.custo || 0];
        }),
        [],
        ['SUBTOTAL', '', '', '', orcamento.subA, '', orcamento.subB],
        ['Mediana A vs B', '', '', '', '', '', orcamento.med],
        ['Delta A vs B (%)', '', '', '', '', '', `${orcamento.deltaAB.toFixed(1)}%`],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba3), 'Orçamento');
    }

    // Aba 4 · Fontes consolidadas
    const fonteCount = {};
    Object.values(fontes).forEach(f => { fonteCount[f] = (fonteCount[f]||0) + 1; });
    const aba4 = [
      ['Auditoria de Fontes · Cap 16'],
      [],
      ['Tipo', 'Quantidade'],
      ['Extraído', fonteCount.extraido || 0],
      ['Preset', fonteCount.preset || 0],
      ['Import', fonteCount.import || 0],
      ['Manual', fonteCount.manual || 0],
      ['Vazio', 40 - Object.keys(fontes).length],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba4), 'Fontes');

    const wbout = XLSX.write(wb, { bookType:'xlsx', type:'array' });
    downloadBlob(new Blob([wbout], {type:'application/octet-stream'}),
      `Manta_${nomeEstudo}_${dataHoje}_MemoriaCalculo.xlsx`);
    flash('A3 · Memória de Cálculo baixada (Excel · 4 abas)');
  };

  // ── A4 · Snapshot HTML standalone ───────────────────────────────────
  const gerarA4 = () => {
    const tabQuali = htmlTabelaKPIs('KPIs Qualitativos · 22 campos', KPIS_QUALI_SCHEMA, qual, fontes);
    const tabQuanti = htmlTabelaKPIs('KPIs Quantitativos · 18 campos', KPIS_QUANTI_SCHEMA, kpis, fontes);

    const top5 = similaridades.slice(0, 5);

    const corpo = `
      <h2>Indicadores Físicos Principais</h2>
      <div>
        ${[
          {l:'Extensão', v:kpis.extKm, u:'km'},
          {l:'Duplicação', v:kpis.dupKm, u:'km'},
          {l:'Pista simples', v:kpis.extPistaSimples, u:'km'},
          {l:'Pista dupla', v:kpis.extPistaDupla, u:'km'},
          {l:'OAEs', v:kpis.oaeQt, u:'un'},
          {l:'Interseções', v:kpis.interQt, u:'un'},
          {l:'Passarelas', v:kpis.passQt, u:'un'},
          {l:'Acessos', v:kpis.acessosQt, u:'un'},
          {l:'Travessias', v:kpis.travUrbanasQt, u:'un'},
          {l:'Ciclovias', v:kpis.ciclKm, u:'km'},
        ].map(k => `<div class="kpi-card"><div class="l">${k.l}</div><div class="v">${k.v || 0}<span class="u">${k.u}</span></div></div>`).join('')}
      </div>

      ${tabQuali}
      ${tabQuanti}

      ${top5.length > 0 ? `<h2>Top 5 Casos Similares</h2>
      <table>
        <tr><th>#</th><th>Lote</th><th>UF</th><th>Score</th><th>Drivers</th></tr>
        ${top5.map((s,i) => `<tr><td>${i+1}</td><td>${s.lote.sigla || s.lote.sig}</td><td>${s.lote.uf || '—'}</td><td><b>${s.score}%</b></td><td style="font-size:10.5px">${(s.drivers||[]).join(' · ')}</td></tr>`).join('')}
      </table>` : ''}
    `;

    const html = wrapHtmlManta(`Snapshot · ${qual.nome || qual.codManta}`, corpo);
    downloadBlob(new Blob([html], {type:'text/html'}),
      `Manta_${nomeEstudo}_${dataHoje}_Snapshot.html`);
    flash('A4 · Snapshot HTML standalone baixado (abra em qualquer navegador)');
  };

  // ── A5 · Backup JSON ────────────────────────────────────────────────
  const gerarA5 = () => {
    const payload = {
      _meta: {
        titulo: qual.nome || qual.codManta,
        gerado_em: new Date().toISOString(),
        portal_versao: 'v28.6',
        total_quali_preenchidos: Object.values(KPIS_QUALI_SCHEMA).flatMap(b=>b.campos).filter(c => qual[c.k] && qual[c.k] !== '').length,
        total_quanti_preenchidos: Object.values(KPIS_QUANTI_SCHEMA).flatMap(b=>b.campos).filter(c => (kpis[c.k]||0) > 0).length,
      },
      qual,
      kpis,
      fontes,
      orcamento: orcamento || null,
      similaridades_top10: similaridades.slice(0, 10).map(s => ({
        sig: s.sig, sigla: s.lote.sigla, score: s.score, drivers: s.drivers,
      })),
    };
    const json = JSON.stringify(payload, null, 2);
    downloadBlob(new Blob([json], {type:'application/json'}),
      `Manta_${nomeEstudo}_${dataHoje}_Backup.json`);
    flash('A5 · Backup JSON baixado (re-importável no portal)');
  };

  // ═══════════════════════════════════════════════════════════════════
  // GRUPO B · CONSOLIDADOS DO PORTFÓLIO (B1-B5)
  // ═══════════════════════════════════════════════════════════════════

  // Universo: estudos salvos + estudo atual + lotes oficiais
  const universoEstudos = useMemo(() => {
    const atual = {
      sig: 'ATUAL', sigla: qual.codManta || 'Atual', uf: qual.ufPrincipal,
      pu: {}, // sem PU calculado se não tiver orçamento
      extKm: kpis.extKm, dupKm: kpis.dupKm, oaeQt: kpis.oaeQt,
      interQt: kpis.interQt, passQt: kpis.passQt, _atual: true,
    };
    return [atual, ...salvosHist, ...baseLotes];
  }, [qual, kpis, salvosHist, baseLotes]);

  // ── B1 · Comparativo de Lotes (Excel + PDF) ─────────────────────────
  const gerarB1 = () => {
    if (universoEstudos.length < 2) { alert('Necessário pelo menos 2 estudos no portfólio.'); return; }

    // Excel
    const wb = XLSX.utils.book_new();
    const aba1 = [
      ['Lote', 'UF', 'Ext (km)', 'Dup (km)', 'OAE (un)', 'Inter (un)', 'Pass (un)', 'Atual?'],
      ...universoEstudos.map(l => [
        l.sigla || l.sig, l.uf || '', l.extKm || 0, l.dupKm || 0,
        l.oaeQt || 0, l.interQt || 0, l.passQt || 0,
        l._atual ? 'SIM' : '',
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba1), 'Comparativo');
    const wbout = XLSX.write(wb, { bookType:'xlsx', type:'array' });
    downloadBlob(new Blob([wbout], {type:'application/octet-stream'}),
      `Manta_B1_Comparativo_${dataHoje}.xlsx`);

    // PDF tabela
    const corpo = `
      <h2>Comparativo Lado a Lado · ${universoEstudos.length} estudos</h2>
      <table>
        <tr><th>Lote</th><th>UF</th><th>Ext (km)</th><th>Dup (km)</th><th>OAE</th><th>Inter</th><th>Pass</th></tr>
        ${universoEstudos.map(l => `<tr style="${l._atual ? 'background:#FEF3C7' : ''}">
          <td>${l.sigla || l.sig}${l._atual ? ' <span class="badge b-extraido">ATUAL</span>' : ''}</td>
          <td style="text-align:center">${l.uf || '—'}</td>
          <td style="text-align:right">${(l.extKm || 0).toFixed(1)}</td>
          <td style="text-align:right">${(l.dupKm || 0).toFixed(1)}</td>
          <td style="text-align:right">${l.oaeQt || 0}</td>
          <td style="text-align:right">${l.interQt || 0}</td>
          <td style="text-align:right">${l.passQt || 0}</td>
        </tr>`).join('')}
      </table>
    `;
    const html = wrapHtmlManta('B1 · Comparativo de Lotes', corpo);
    abrirParaImprimir(html, 'Comparativo de Lotes');
    flash(`B1 · Comparativo baixado (Excel) e aberto para impressão (${universoEstudos.length} estudos)`);
  };

  // ── B2 · Dashboard Portfólio (HTML interativo) ──────────────────────
  const gerarB2 = () => {
    if (universoEstudos.length < 1) { alert('Sem estudos no portfólio.'); return; }
    const totalExt = universoEstudos.reduce((s,l) => s + (l.extKm||0), 0);
    const totalDup = universoEstudos.reduce((s,l) => s + (l.dupKm||0), 0);
    const totalOae = universoEstudos.reduce((s,l) => s + (l.oaeQt||0), 0);
    const totalInter = universoEstudos.reduce((s,l) => s + (l.interQt||0), 0);
    const ufs = {};
    universoEstudos.forEach(l => { if (l.uf) ufs[l.uf] = (ufs[l.uf]||0) + 1; });

    const corpo = `
      <h2>Visão Executiva</h2>
      <div>
        <div class="kpi-card"><div class="l">Estudos no portfólio</div><div class="v">${universoEstudos.length}</div></div>
        <div class="kpi-card"><div class="l">Extensão total</div><div class="v">${totalExt.toFixed(0)}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">Duplicação total</div><div class="v">${totalDup.toFixed(0)}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">OAEs total</div><div class="v">${totalOae}</div></div>
        <div class="kpi-card"><div class="l">Interseções</div><div class="v">${totalInter}</div></div>
        <div class="kpi-card"><div class="l">UFs cobertas</div><div class="v">${Object.keys(ufs).length}</div></div>
      </div>

      <h2>Distribuição por UF</h2>
      <table>
        <tr><th>UF</th><th>Quantidade de estudos</th><th>% do portfólio</th></tr>
        ${Object.entries(ufs).sort((a,b)=>b[1]-a[1]).map(([uf, n]) => `<tr>
          <td>${uf}</td><td>${n}</td><td>${((n/universoEstudos.length)*100).toFixed(1)}%</td>
        </tr>`).join('')}
      </table>

      <h2>Médias do Portfólio</h2>
      <table>
        <tr><th>Métrica</th><th>Média</th><th>Atual</th><th>Δ</th></tr>
        <tr><td>Extensão</td><td>${(totalExt/universoEstudos.length).toFixed(1)} km</td><td>${kpis.extKm} km</td><td>${((kpis.extKm/(totalExt/universoEstudos.length)-1)*100).toFixed(1)}%</td></tr>
        <tr><td>Duplicação</td><td>${(totalDup/universoEstudos.length).toFixed(1)} km</td><td>${kpis.dupKm} km</td><td>${kpis.dupKm > 0 ? ((kpis.dupKm/(totalDup/universoEstudos.length)-1)*100).toFixed(1) + '%' : '—'}</td></tr>
        <tr><td>OAEs</td><td>${(totalOae/universoEstudos.length).toFixed(0)}</td><td>${kpis.oaeQt}</td><td>${kpis.oaeQt > 0 ? ((kpis.oaeQt/(totalOae/universoEstudos.length)-1)*100).toFixed(1) + '%' : '—'}</td></tr>
        <tr><td>Interseções</td><td>${(totalInter/universoEstudos.length).toFixed(0)}</td><td>${kpis.interQt}</td><td>${kpis.interQt > 0 ? ((kpis.interQt/(totalInter/universoEstudos.length)-1)*100).toFixed(1) + '%' : '—'}</td></tr>
      </table>

      <h2>Lista Completa</h2>
      <table>
        <tr><th>Lote</th><th>UF</th><th>Ext</th><th>Dup</th><th>OAE</th><th>Inter</th></tr>
        ${universoEstudos.map(l => `<tr style="${l._atual ? 'background:#FEF3C7' : ''}">
          <td>${l.sigla || l.sig}${l._atual ? ' <span class="badge b-extraido">ATUAL</span>' : ''}</td>
          <td>${l.uf || '—'}</td>
          <td>${(l.extKm||0).toFixed(1)}</td>
          <td>${(l.dupKm||0).toFixed(1)}</td>
          <td>${l.oaeQt||0}</td>
          <td>${l.interQt||0}</td>
        </tr>`).join('')}
      </table>
    `;
    const html = wrapHtmlManta('B2 · Dashboard do Portfólio', corpo, {meta:`${universoEstudos.length} estudos`});
    downloadBlob(new Blob([html], {type:'text/html'}),
      `Manta_B2_Dashboard_${dataHoje}.html`);
    flash(`B2 · Dashboard baixado (${universoEstudos.length} estudos · abrir no navegador)`);
  };

  // ── B3 · Matriz de Similaridade (Excel) ─────────────────────────────
  const gerarB3 = () => {
    if (universoEstudos.length < 2) { alert('Necessário pelo menos 2 estudos.'); return; }
    // Vetor de features físicas normalizado
    const feat = (l) => {
      const e = l.extKm || 1;
      return [(l.dupKm||0)/e, (l.oaeQt||0)/e, (l.interQt||0)/e, (l.passQt||0)/e];
    };
    const N = universoEstudos.length;
    const aoa = [['', ...universoEstudos.map(l => l.sigla || l.sig)]];
    for (let i = 0; i < N; i++) {
      const row = [universoEstudos[i].sigla || universoEstudos[i].sig];
      for (let j = 0; j < N; j++) {
        row.push(i === j ? 1.000 : Number(cossenoVec(feat(universoEstudos[i]), feat(universoEstudos[j])).toFixed(3)));
      }
      aoa.push(row);
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Matriz Cosseno');
    const wbout = XLSX.write(wb, { bookType:'xlsx', type:'array' });
    downloadBlob(new Blob([wbout], {type:'application/octet-stream'}),
      `Manta_B3_MatrizSimilaridade_${dataHoje}.xlsx`);
    flash(`B3 · Matriz ${N}×${N} de similaridade baixada (Excel)`);
  };

  // ── B4 · Auditoria de Fontes (PDF) ──────────────────────────────────
  const gerarB4 = () => {
    const fonteCount = {};
    Object.values(fontes).forEach(f => { fonteCount[f] = (fonteCount[f]||0) + 1; });
    const totalCampos = 40;
    const totalPreench = Object.keys(fontes).length;
    const score = Math.round((totalPreench / totalCampos) * 100);

    // Para cada campo, listar status
    const linhasQuali = Object.entries(KPIS_QUALI_SCHEMA).flatMap(([bk, b]) => b.campos.map(c => {
      const f = fontes[c.k];
      const v = qual[c.k];
      const preench = v && v !== '' && v !== 0;
      return `<tr>
        <td>${b.titulo.split('·')[0].trim()}</td>
        <td>${c.label}</td>
        <td style="text-align:center">${preench ? '✓' : '<span style="color:#9CA3AF">—</span>'}</td>
        <td style="text-align:center">${f ? `<span class="badge b-${f === 'extraido' ? 'extraido' : f}">${f}</span>` : '<span class="b-empty">vazio</span>'}</td>
      </tr>`;
    })).join('');

    const linhasQuanti = Object.entries(KPIS_QUANTI_SCHEMA).flatMap(([bk, g]) => g.campos.map(c => {
      const f = fontes[c.k];
      const v = kpis[c.k];
      const preench = (v || 0) > 0;
      return `<tr>
        <td>${g.titulo.split('·')[0].trim()}</td>
        <td>${c.label}</td>
        <td style="text-align:center">${preench ? '✓' : '<span style="color:#9CA3AF">—</span>'}</td>
        <td style="text-align:center">${f ? `<span class="badge b-${f === 'extraido' ? 'extraido' : f}">${f}</span>` : '<span class="b-empty">vazio</span>'}</td>
      </tr>`;
    })).join('');

    const corpo = `
      <h2>Score Geral de Auditoria</h2>
      <div>
        <div class="kpi-card"><div class="l">Score</div><div class="v" style="color:${score>70?'#10B981':(score>30?'#F59E0B':'#EF4444')}">${score}%</div></div>
        <div class="kpi-card"><div class="l">Preenchidos</div><div class="v">${totalPreench}/${totalCampos}</div></div>
        <div class="kpi-card"><div class="l">Extraídos</div><div class="v" style="color:#10B981">${fonteCount.extraido || 0}</div></div>
        <div class="kpi-card"><div class="l">Preset</div><div class="v" style="color:#F59E0B">${fonteCount.preset || 0}</div></div>
        <div class="kpi-card"><div class="l">Manual</div><div class="v">${fonteCount.manual || 0}</div></div>
        <div class="kpi-card"><div class="l">Import</div><div class="v" style="color:#3B82F6">${fonteCount.import || 0}</div></div>
      </div>

      <h2>Detalhamento · KPIs Qualitativos</h2>
      <table>
        <tr><th>Bloco</th><th>Campo</th><th>Preenchido</th><th>Fonte</th></tr>
        ${linhasQuali}
      </table>

      <h2>Detalhamento · KPIs Quantitativos</h2>
      <table>
        <tr><th>Bloco</th><th>Campo</th><th>Preenchido</th><th>Fonte</th></tr>
        ${linhasQuanti}
      </table>

      <h2>Recomendações Cap 16</h2>
      <ul style="margin-left:20px;font-size:11.5px;line-height:1.6">
        ${score < 50 ? '<li style="color:#EF4444">Score < 50%: dados insuficientes para auditoria · refazer extração</li>' : ''}
        ${(fonteCount.manual || 0) > (fonteCount.extraido || 0) ? '<li style="color:#F59E0B">Maioria dos dados é manual · validar contra fonte primária</li>' : ''}
        ${(fonteCount.preset || 0) > 15 ? '<li style="color:#F59E0B">Mais de 15 campos vêm de preset · trocar por dados reais antes de fechar orçamento</li>' : ''}
        ${(fonteCount.extraido || 0) >= 20 ? '<li style="color:#10B981">✓ Pelo menos 20 campos extraídos de fonte primária · qualidade boa</li>' : ''}
      </ul>
    `;
    const html = wrapHtmlManta(`B4 · Auditoria de Fontes · ${qual.nome || qual.codManta}`, corpo, {meta:`Score ${score}%`});
    abrirParaImprimir(html, 'Auditoria de Fontes');
    flash(`B4 · Auditoria aberta para impressão (score ${score}%)`);
  };

  // ── B5 · Curvas de Preços Unitários (Excel) ─────────────────────────
  const gerarB5 = () => {
    if (universoEstudos.length < 2) { alert('Necessário pelo menos 2 estudos com PU.'); return; }
    const wb = XLSX.utils.book_new();
    const categorias = ['dup', 'marg', 'cicl', 'oae', 'pass', 'inter'];
    const catLabels = {dup:'Duplicação', marg:'Marginais', cicl:'Ciclovias', oae:'OAE', pass:'Passarelas', inter:'Interseções'};
    const aba1 = [
      ['Lote', ...categorias.map(c => catLabels[c])],
      ...universoEstudos.filter(l => l.pu).map(l => [
        l.sigla || l.sig,
        ...categorias.map(c => l.pu[c] || 0),
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba1), 'PU por Lote');

    // Aba estatística
    const aba2 = [['Categoria', 'Mínimo', 'Máximo', 'Média', 'Mediana']];
    categorias.forEach(c => {
      const vals = universoEstudos.filter(l => l.pu && l.pu[c]).map(l => l.pu[c]).sort((a,b)=>a-b);
      if (vals.length === 0) { aba2.push([catLabels[c], 0, 0, 0, 0]); return; }
      const min = vals[0], max = vals[vals.length-1];
      const avg = vals.reduce((s,v)=>s+v, 0) / vals.length;
      const med = vals[Math.floor(vals.length/2)];
      aba2.push([catLabels[c], min, max, Math.round(avg), med]);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba2), 'Estatística');

    const wbout = XLSX.write(wb, { bookType:'xlsx', type:'array' });
    downloadBlob(new Blob([wbout], {type:'application/octet-stream'}),
      `Manta_B5_CurvasPU_${dataHoje}.xlsx`);
    flash(`B5 · Curvas de PU baixado (Excel · ${universoEstudos.filter(l=>l.pu).length} lotes com PU)`);
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER · v28.8 · 10 relatórios em 6 sub-categorias + preview lateral
  // ═══════════════════════════════════════════════════════════════════
  const gerarHtmlA1 = () => {
    const tabQuali = htmlTabelaKPIs('KPIs Qualitativos · 22 campos', KPIS_QUALI_SCHEMA, qual, fontes);
    const tabQuanti = htmlTabelaKPIs('KPIs Quantitativos · 18 campos', KPIS_QUANTI_SCHEMA, kpis, fontes);
    const fc = {};
    Object.values(fontes).forEach(f => { fc[f] = (fc[f]||0) + 1; });
    const corpo = `
      <h2>1 · Identificação do Estudo</h2>
      <table>
        <tr><th style="width:30%">Campo</th><th>Valor</th></tr>
        <tr><td>Nome</td><td>${qual.nome || '—'}</td></tr>
        <tr><td>Código Manta</td><td>${qual.codManta || '—'}</td></tr>
        <tr><td>UF Principal</td><td>${qual.ufPrincipal || qual.uf || '—'}</td></tr>
        <tr><td>Cidade</td><td>${qual.cidade || '—'}</td></tr>
        <tr><td>Tipo de Obra</td><td>${qual.tipoObra || '—'}</td></tr>
        <tr><td>Concessionária</td><td>${qual.concessionaria || '—'}</td></tr>
        <tr><td>Regulador</td><td>${qual.regulador || '—'}</td></tr>
        <tr><td>Prazo de Concessão</td><td>${qual.prazoAnos || '—'} anos</td></tr>
        <tr><td>Data do Estudo</td><td>${qual.dataEstudo || '—'}</td></tr>
        <tr><td>Nível de Detalhe</td><td>${qual.nivelDetalhe || '—'}</td></tr>
      </table>
      <h2>2 · Indicadores Físicos Principais</h2>
      <div>
        <div class="kpi-card"><div class="l">Extensão</div><div class="v">${kpis.extKm || 0}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">Duplicação</div><div class="v">${kpis.dupKm || 0}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">OAEs</div><div class="v">${kpis.oaeQt || 0}<span class="u">un</span></div></div>
        <div class="kpi-card"><div class="l">Interseções</div><div class="v">${kpis.interQt || 0}<span class="u">un</span></div></div>
        <div class="kpi-card"><div class="l">Passarelas</div><div class="v">${kpis.passQt || 0}<span class="u">un</span></div></div>
        <div class="kpi-card"><div class="l">Acessos</div><div class="v">${kpis.acessosQt || 0}<span class="u">un</span></div></div>
      </div>
      ${tabQuali}
      ${tabQuanti}
      <h2>3 · Auditoria de Fontes (Cap 16)</h2>
      <table>
        <tr><th>Tipo de Fonte</th><th>Quantidade</th></tr>
        <tr><td>🟢 Extraído</td><td>${fc.extraido || 0}</td></tr>
        <tr><td>🟡 Preset</td><td>${fc.preset || 0}</td></tr>
        <tr><td>🔵 Import</td><td>${fc.import || 0}</td></tr>
        <tr><td>⚪ Manual</td><td>${fc.manual || 0}</td></tr>
      </table>`;
    return wrapHtmlManta(`Ficha Técnica · ${qual.nome || qual.codManta || 'Estudo'}`, corpo, {
      meta: `${kpis.extKm} km · ${qual.ufPrincipal}`
    });
  };

  const gerarHtmlA2 = () => {
    const top3 = similaridades.slice(0, 3);
    const orcStr = orcamento ? `R$ ${(orcamento.med/1e6).toFixed(0)} milhões` : 'pendente · escolha caso de referência';
    const corpo = `
      <h2>Síntese</h2>
      <p style="margin:8px 0;font-size:12px;line-height:1.5">
        <b>${qual.nome || qual.codManta}</b> é um(a) <b>${qual.tipoObra || 'projeto'}</b>
        ${qual.ufPrincipal ? 'no estado <b>' + qual.ufPrincipal + '</b>' : ''}
        com extensão total de <b>${kpis.extKm || 0} km</b>, sendo <b>${kpis.dupKm || 0} km</b> de duplicação,
        <b>${kpis.oaeQt || 0}</b> OAEs e <b>${kpis.interQt || 0}</b> interseções.
        ${qual.concessionaria ? 'Concessionária: <b>' + qual.concessionaria + '</b>.' : ''}
        ${qual.prazoAnos ? 'Prazo: <b>' + qual.prazoAnos + ' anos</b>.' : ''}
      </p>
      <h2>Estimativa de Orçamento</h2>
      <p style="margin:8px 0;font-size:14px"><b>${orcStr}</b></p>
      ${orcamento ? `<p style="font-size:10.5px;color:#6B7280">Δ Rota A vs B: ${orcamento.deltaAB.toFixed(1)}%</p>` : ''}
      <h2>Top 3 Casos Similares</h2>
      ${top3.length === 0 ? '<p style="color:#9CA3AF;font-style:italic">— Buscar similaridade no fluxo Novo Estudo → Orçamentação</p>' : `
      <table>
        <tr><th>#</th><th>Lote</th><th>UF</th><th>Score</th><th>Drivers</th></tr>
        ${top3.map((s,i) => `<tr><td>${i+1}</td><td>${s.lote.sigla || s.lote.sig}</td><td>${s.lote.uf || '—'}</td><td><b>${s.score}%</b></td><td style="font-size:10.5px">${(s.drivers||[]).join(' · ') || '—'}</td></tr>`).join('')}
      </table>`}
      <h2>Próximos Passos</h2>
      <ol style="margin-left:20px;font-size:11.5px;line-height:1.6">
        <li>Validar quantitativos físicos pendentes (campos vazios)</li>
        <li>Buscar similaridade ampliada com filtros</li>
        <li>Refinar BDI específico da concessionária</li>
        <li>Consolidar com retificação contratual se houver</li>
      </ol>`;
    return wrapHtmlManta(`Resumo Executivo · ${qual.nome || qual.codManta}`, corpo);
  };

  const gerarHtmlA3 = () => {
    const linhasQuali = Object.values(KPIS_QUALI_SCHEMA).flatMap(b => b.campos.map(c =>
      `<tr><td>${c.label}</td><td>${qual[c.k] ?? '—'}</td><td><span class="badge b-${fontes[c.k] === 'extraido' ? 'extraido' : (fontes[c.k] || 'manual')}">${fontes[c.k] || '—'}</span></td></tr>`
    )).join('');
    const linhasQuanti = Object.entries(KPIS_QUANTI_SCHEMA).flatMap(([k,g]) => g.campos.map(c =>
      `<tr><td>${g.titulo.split('·')[0].trim()}</td><td>${c.label}</td><td>${c.sufixo || '—'}</td><td style="text-align:right">${kpis[c.k] ?? 0}</td><td><span class="badge b-${fontes[c.k] === 'extraido' ? 'extraido' : (fontes[c.k] || 'manual')}">${fontes[c.k] || '—'}</span></td></tr>`
    )).join('');
    const corpo = `
      <h2>Aba 1 · Identificação Qualitativa</h2>
      <table>
        <tr><th>Campo</th><th>Valor</th><th>Fonte</th></tr>
        ${linhasQuali}
      </table>
      <h2>Aba 2 · KPIs Quantitativos</h2>
      <table>
        <tr><th>Bloco</th><th>Campo</th><th>Unid.</th><th>Valor</th><th>Fonte</th></tr>
        ${linhasQuanti}
      </table>
      <h2>Aba 3 · Orçamento</h2>
      ${orcamento && orcamento.rotaA ? `<table>
        <tr><th>Categoria</th><th>Qtd</th><th>Unid.</th><th>PU A (R$)</th><th>Custo A</th><th>PU B (R$)</th><th>Custo B</th></tr>
        ${orcamento.rotaA.map((r,i)=>{const rb=orcamento.rotaB[i]||{};return `<tr><td>${r.l}</td><td>${r.q}</td><td>${r.und}</td><td style="text-align:right">${r.pu}</td><td style="text-align:right">${r.custo}</td><td style="text-align:right">${rb.pu||0}</td><td style="text-align:right">${rb.custo||0}</td></tr>`}).join('')}
        <tr style="font-weight:700;background:#F3F4F6"><td colspan="4">SUBTOTAL</td><td style="text-align:right">${orcamento.subA}</td><td></td><td style="text-align:right">${orcamento.subB}</td></tr>
        <tr style="font-weight:700"><td colspan="6">Mediana A vs B</td><td style="text-align:right">${orcamento.med}</td></tr>
        <tr style="font-weight:700"><td colspan="6">Δ A vs B (%)</td><td style="text-align:right">${orcamento.deltaAB.toFixed(1)}%</td></tr>
      </table>` : '<p style="color:#9CA3AF;font-style:italic">Sem orçamento calculado · gerar no fluxo Novo Estudo → Orçamentação</p>'}`;
    return wrapHtmlManta(`Memória de Cálculo · ${qual.nome || qual.codManta}`, corpo);
  };

  const gerarHtmlA4 = () => {
    // O snapshot HTML JÁ é gerado pelo gerarA4 · usar a mesma lógica em memória
    const tabQuali = htmlTabelaKPIs('KPIs Qualitativos · 22 campos', KPIS_QUALI_SCHEMA, qual, fontes);
    const tabQuanti = htmlTabelaKPIs('KPIs Quantitativos · 18 campos', KPIS_QUANTI_SCHEMA, kpis, fontes);
    const top5 = similaridades.slice(0, 5);
    const corpo = `
      <h2>Indicadores Físicos Principais</h2>
      <div>
        ${[
          {l:'Extensão', v:kpis.extKm, u:'km'},
          {l:'Duplicação', v:kpis.dupKm, u:'km'},
          {l:'OAEs', v:kpis.oaeQt, u:'un'},
          {l:'Interseções', v:kpis.interQt, u:'un'},
          {l:'Passarelas', v:kpis.passQt, u:'un'},
          {l:'Acessos', v:kpis.acessosQt, u:'un'},
        ].map(k => `<div class="kpi-card"><div class="l">${k.l}</div><div class="v">${k.v || 0}<span class="u">${k.u}</span></div></div>`).join('')}
      </div>
      ${tabQuali}
      ${tabQuanti}
      ${top5.length > 0 ? `<h2>Top 5 Casos Similares</h2>
      <table>
        <tr><th>#</th><th>Lote</th><th>UF</th><th>Score</th></tr>
        ${top5.map((s,i) => `<tr><td>${i+1}</td><td>${s.lote.sigla || s.lote.sig}</td><td>${s.lote.uf || '—'}</td><td><b>${s.score}%</b></td></tr>`).join('')}
      </table>` : ''}`;
    return wrapHtmlManta(`Snapshot · ${qual.nome || qual.codManta}`, corpo);
  };

  const gerarHtmlA5 = () => {
    const payload = {
      _meta: {
        titulo: qual.nome || qual.codManta,
        gerado_em: new Date().toISOString(),
        portal_versao: 'v28.8',
      },
      qual, kpis, fontes, orcamento: orcamento || null,
      similaridades_top10: similaridades.slice(0, 10).map(s => ({
        sig: s.sig, sigla: s.lote.sigla, score: s.score, drivers: s.drivers,
      })),
    };
    const json = JSON.stringify(payload, null, 2);
    const corpo = `
      <h2>Backup JSON · Re-importável no Portal</h2>
      <p style="font-size:11.5px;color:#6B7280">Conteúdo do JSON gerado · pode ser colado de volta no portal pela opção "Importar JSON" no fluxo Novo Estudo.</p>
      <pre style="background:#1F2937;color:#D1FAE5;padding:14px;border-radius:4px;font-size:10.5px;max-height:500px;overflow:auto;font-family:'Courier New',monospace">${json.replace(/</g, '&lt;')}</pre>`;
    return wrapHtmlManta(`Backup JSON · ${qual.nome || qual.codManta}`, corpo);
  };

  const gerarHtmlB1 = () => {
    if (universoEstudos.length < 2) return wrapHtmlManta('B1 · Comparativo', '<p style="color:#9CA3AF;font-style:italic">Necessário pelo menos 2 estudos no portfólio.</p>');
    const corpo = `
      <h2>Comparativo Lado a Lado · ${universoEstudos.length} estudos</h2>
      <table>
        <tr><th>Lote</th><th>UF</th><th>Ext (km)</th><th>Dup (km)</th><th>OAE</th><th>Inter</th><th>Pass</th></tr>
        ${universoEstudos.map(l => `<tr style="${l._atual ? 'background:#FEF3C7' : ''}">
          <td>${l.sigla || l.sig}${l._atual ? ' <span class="badge b-extraido">ATUAL</span>' : ''}</td>
          <td style="text-align:center">${l.uf || '—'}</td>
          <td style="text-align:right">${(l.extKm || 0).toFixed(1)}</td>
          <td style="text-align:right">${(l.dupKm || 0).toFixed(1)}</td>
          <td style="text-align:right">${l.oaeQt || 0}</td>
          <td style="text-align:right">${l.interQt || 0}</td>
          <td style="text-align:right">${l.passQt || 0}</td>
        </tr>`).join('')}
      </table>`;
    return wrapHtmlManta('B1 · Comparativo de Lotes', corpo);
  };

  const gerarHtmlB2 = () => {
    const totalExt = universoEstudos.reduce((s,l) => s + (l.extKm||0), 0);
    const totalDup = universoEstudos.reduce((s,l) => s + (l.dupKm||0), 0);
    const totalOae = universoEstudos.reduce((s,l) => s + (l.oaeQt||0), 0);
    const totalInter = universoEstudos.reduce((s,l) => s + (l.interQt||0), 0);
    const ufs = {};
    universoEstudos.forEach(l => { if (l.uf) ufs[l.uf] = (ufs[l.uf]||0) + 1; });
    const corpo = `
      <h2>Visão Executiva</h2>
      <div>
        <div class="kpi-card"><div class="l">Estudos</div><div class="v">${universoEstudos.length}</div></div>
        <div class="kpi-card"><div class="l">Ext. total</div><div class="v">${totalExt.toFixed(0)}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">Dup. total</div><div class="v">${totalDup.toFixed(0)}<span class="u">km</span></div></div>
        <div class="kpi-card"><div class="l">OAEs total</div><div class="v">${totalOae}</div></div>
        <div class="kpi-card"><div class="l">Interseções</div><div class="v">${totalInter}</div></div>
        <div class="kpi-card"><div class="l">UFs</div><div class="v">${Object.keys(ufs).length}</div></div>
      </div>
      <h2>Distribuição por UF</h2>
      <table>
        <tr><th>UF</th><th>Estudos</th><th>% portfolio</th></tr>
        ${Object.entries(ufs).sort((a,b)=>b[1]-a[1]).map(([uf, n]) => `<tr><td>${uf}</td><td>${n}</td><td>${((n/universoEstudos.length)*100).toFixed(1)}%</td></tr>`).join('')}
      </table>
      <h2>Lista Completa</h2>
      <table>
        <tr><th>Lote</th><th>UF</th><th>Ext</th><th>Dup</th><th>OAE</th><th>Inter</th></tr>
        ${universoEstudos.map(l => `<tr style="${l._atual ? 'background:#FEF3C7' : ''}">
          <td>${l.sigla || l.sig}${l._atual ? ' <span class="badge b-extraido">ATUAL</span>' : ''}</td>
          <td>${l.uf || '—'}</td><td>${(l.extKm||0).toFixed(1)}</td>
          <td>${(l.dupKm||0).toFixed(1)}</td><td>${l.oaeQt||0}</td><td>${l.interQt||0}</td>
        </tr>`).join('')}
      </table>`;
    return wrapHtmlManta('B2 · Dashboard do Portfólio', corpo, {meta:`${universoEstudos.length} estudos`});
  };

  const gerarHtmlB3 = () => {
    if (universoEstudos.length < 2) return wrapHtmlManta('B3 · Matriz Similaridade', '<p style="color:#9CA3AF;font-style:italic">Necessário pelo menos 2 estudos.</p>');
    const feat = (l) => {
      const e = l.extKm || 1;
      return [(l.dupKm||0)/e, (l.oaeQt||0)/e, (l.interQt||0)/e, (l.passQt||0)/e];
    };
    const N = universoEstudos.length;
    let tabela = '<tr><th></th>';
    for (let j = 0; j < N; j++) tabela += `<th>${(universoEstudos[j].sigla || universoEstudos[j].sig).slice(0, 8)}</th>`;
    tabela += '</tr>';
    for (let i = 0; i < N; i++) {
      tabela += `<tr><th>${(universoEstudos[i].sigla || universoEstudos[i].sig).slice(0, 12)}</th>`;
      for (let j = 0; j < N; j++) {
        const v = i === j ? 1.0 : cossenoVec(feat(universoEstudos[i]), feat(universoEstudos[j]));
        const bg = v > 0.9 ? '#10B981' : v > 0.7 ? '#84CC16' : v > 0.5 ? '#FBBF24' : v > 0.3 ? '#F97316' : '#EF4444';
        tabela += `<td style="background:${bg};color:#fff;text-align:center;font-weight:700">${v.toFixed(2)}</td>`;
      }
      tabela += '</tr>';
    }
    const corpo = `<h2>Matriz de Similaridade ${N}×${N} · Cosseno</h2>
      <p style="font-size:11px;color:#6B7280">Verde = alta similaridade · vermelho = baixa</p>
      <table>${tabela}</table>`;
    return wrapHtmlManta(`B3 · Matriz Similaridade ${N}×${N}`, corpo);
  };

  const gerarHtmlB4 = () => {
    const fc = {};
    Object.values(fontes).forEach(f => { fc[f] = (fc[f]||0) + 1; });
    const totalCampos = 40;
    const totalPreench = Object.keys(fontes).length;
    const score = Math.round((totalPreench / totalCampos) * 100);
    const corpo = `
      <h2>Score Geral de Auditoria · Cap 16</h2>
      <div>
        <div class="kpi-card"><div class="l">Score</div><div class="v" style="color:${score>70?'#10B981':(score>30?'#F59E0B':'#EF4444')}">${score}%</div></div>
        <div class="kpi-card"><div class="l">Preench.</div><div class="v">${totalPreench}/${totalCampos}</div></div>
        <div class="kpi-card"><div class="l">Extraídos</div><div class="v" style="color:#10B981">${fc.extraido || 0}</div></div>
        <div class="kpi-card"><div class="l">Preset</div><div class="v" style="color:#F59E0B">${fc.preset || 0}</div></div>
        <div class="kpi-card"><div class="l">Manual</div><div class="v">${fc.manual || 0}</div></div>
        <div class="kpi-card"><div class="l">Import</div><div class="v" style="color:#3B82F6">${fc.import || 0}</div></div>
      </div>
      <h2>Recomendações</h2>
      <ul style="margin-left:20px;font-size:11.5px;line-height:1.6">
        ${score < 50 ? '<li style="color:#EF4444">Score < 50% · dados insuficientes</li>' : ''}
        ${(fc.manual || 0) > (fc.extraido || 0) ? '<li style="color:#F59E0B">Maioria manual · validar contra fonte primária</li>' : ''}
        ${(fc.preset || 0) > 15 ? '<li style="color:#F59E0B">+15 campos vêm de preset · trocar por dados reais</li>' : ''}
        ${(fc.extraido || 0) >= 20 ? '<li style="color:#10B981">✓ ≥ 20 campos extraídos · qualidade boa</li>' : ''}
      </ul>`;
    return wrapHtmlManta(`B4 · Auditoria · ${qual.nome || qual.codManta}`, corpo, {meta:`Score ${score}%`});
  };

  const gerarHtmlB5 = () => {
    if (universoEstudos.length < 2) return wrapHtmlManta('B5 · Curvas PU', '<p style="color:#9CA3AF;font-style:italic">Necessário pelo menos 2 estudos com PU.</p>');
    const categorias = ['dup', 'marg', 'cicl', 'oae', 'pass', 'inter'];
    const catLabels = {dup:'Duplicação', marg:'Marginais', cicl:'Ciclovias', oae:'OAE', pass:'Passarelas', inter:'Interseções'};
    let linhas = '';
    categorias.forEach(c => {
      const vals = universoEstudos.filter(l => l.pu && l.pu[c]).map(l => l.pu[c]).sort((a,b)=>a-b);
      if (vals.length === 0) {
        linhas += `<tr><td>${catLabels[c]}</td><td>—</td><td>—</td><td>—</td><td>—</td></tr>`;
      } else {
        const min = vals[0], max = vals[vals.length-1];
        const avg = vals.reduce((s,v)=>s+v, 0) / vals.length;
        const med = vals[Math.floor(vals.length/2)];
        linhas += `<tr><td>${catLabels[c]}</td><td>R$ ${min.toLocaleString('pt-BR')}</td><td>R$ ${max.toLocaleString('pt-BR')}</td><td>R$ ${Math.round(avg).toLocaleString('pt-BR')}</td><td>R$ ${med.toLocaleString('pt-BR')}</td></tr>`;
      }
    });
    const corpo = `<h2>Curvas de Preços Unitários · ${universoEstudos.filter(l=>l.pu).length} lotes com PU</h2>
      <table>
        <tr><th>Categoria</th><th>Mínimo</th><th>Máximo</th><th>Média</th><th>Mediana</th></tr>
        ${linhas}
      </table>`;
    return wrapHtmlManta('B5 · Curvas de PU', corpo);
  };

  const relatorios = [
    // GRUPO A · POR ESTUDO ──────────────────────────────────────────────
    {grupo:'A', subgrupo:'documentacao', subgrupoLabel:'📋 Documentação Técnica',
      id:'A1', titulo:'Ficha Técnica', ic:'📄', fmt:'PDF',
      desc:'2-3 páginas · Identificação · 22 KPIs Quali · 18 KPIs Quanti · Auditoria de fontes',
      fn:gerarA1, htmlFn:() => gerarHtmlA1(),
      disabled: !qual.nome && !qual.codManta},
    {grupo:'A', subgrupo:'documentacao', subgrupoLabel:'📋 Documentação Técnica',
      id:'A4', titulo:'Snapshot Interativo', ic:'🌐', fmt:'HTML',
      desc:'Visualização interativa · funciona offline · pode enviar por email',
      fn:gerarA4, htmlFn:() => gerarHtmlA4(),
      disabled: false},

    {grupo:'A', subgrupo:'apresentacao', subgrupoLabel:'💼 Apresentação Executiva',
      id:'A2', titulo:'Resumo Executivo', ic:'📝', fmt:'PDF',
      desc:'1 página · síntese · estimativa · top 3 similares · próximos passos',
      fn:gerarA2, htmlFn:() => gerarHtmlA2(),
      disabled: !qual.nome && !qual.codManta},

    {grupo:'A', subgrupo:'trabalho', subgrupoLabel:'🧾 Trabalho Técnico',
      id:'A3', titulo:'Memória de Cálculo', ic:'🧾', fmt:'Excel',
      desc:'4 abas · Identificação · KPIs Quanti · Orçamento · Fontes consolidadas',
      fn:gerarA3, htmlFn:() => gerarHtmlA3(),
      disabled: false},
    {grupo:'A', subgrupo:'trabalho', subgrupoLabel:'🧾 Trabalho Técnico',
      id:'A5', titulo:'Backup re-importável', ic:'💾', fmt:'JSON',
      desc:'Snapshot completo (qual + kpis + fontes + orçamento + top 10 similares)',
      fn:gerarA5, htmlFn:() => gerarHtmlA5(),
      disabled: false},

    // GRUPO B · CONSOLIDADOS ─────────────────────────────────────────
    {grupo:'B', subgrupo:'comparativos', subgrupoLabel:'📊 Comparativos',
      id:'B1', titulo:'Comparativo de Lotes', ic:'📊', fmt:'PDF + Excel',
      desc:`Lado a lado · ${universoEstudos.length} estudos · com destaque do atual`,
      fn:gerarB1, htmlFn:() => gerarHtmlB1(),
      disabled: universoEstudos.length < 2},
    {grupo:'B', subgrupo:'comparativos', subgrupoLabel:'📊 Comparativos',
      id:'B2', titulo:'Dashboard Portfólio', ic:'📈', fmt:'HTML',
      desc:'Visão executiva · totais · médias · distribuição por UF · pra diretoria',
      fn:gerarB2, htmlFn:() => gerarHtmlB2(),
      disabled: universoEstudos.length < 1},

    {grupo:'B', subgrupo:'quantitativos', subgrupoLabel:'🔢 Análises Quantitativas',
      id:'B3', titulo:'Matriz Similaridade', ic:'🔢', fmt:'Excel',
      desc:`Cosseno N×N entre todos os ${universoEstudos.length} estudos · clusters`,
      fn:gerarB3, htmlFn:() => gerarHtmlB3(),
      disabled: universoEstudos.length < 2},
    {grupo:'B', subgrupo:'quantitativos', subgrupoLabel:'🔢 Análises Quantitativas',
      id:'B5', titulo:'Curvas de Preços', ic:'💹', fmt:'Excel',
      desc:'Min/máx/média/mediana de PU por categoria · benchmark de mercado',
      fn:gerarB5, htmlFn:() => gerarHtmlB5(),
      disabled: universoEstudos.length < 2},

    {grupo:'B', subgrupo:'conformidade', subgrupoLabel:'🛡 Conformidade',
      id:'B4', titulo:'Auditoria Cap 16', ic:'🛡', fmt:'PDF',
      desc:'Score de qualidade · detalhamento por campo · recomendações de melhoria',
      fn:gerarB4, htmlFn:() => gerarHtmlB4(),
      disabled: false},
  ];

  // ─── Geradores HTML universais (todos os 10) ─────────────────────
  // Reusam a lógica dos gerarA1..gerarB5 mas retornam HTML em memória
  // em vez de baixar/imprimir. Usado no preview lateral.


  // Sub-grupos ordenados (ordem visual)
  const ORDEM_SUBGRUPOS_A = ['documentacao', 'apresentacao', 'trabalho'];
  const ORDEM_SUBGRUPOS_B = ['comparativos', 'quantitativos', 'conformidade'];

  // Exportar TUDO (HTML único com todos os 10 relatórios concatenados)
  const exportarTudoHtml = () => {
    const habilitados = relatorios.filter(r => !r.disabled);
    if (habilitados.length === 0) {
      alert('Nenhum relatório disponível no estado atual.');
      return;
    }
    const conf = window.confirm(`Exportar TUDO em HTML único?\n\n${habilitados.length} relatórios serão concatenados em um único arquivo HTML standalone.\n\nContinuar?`);
    if (!conf) return;

    let corpoTudo = '';
    habilitados.forEach(rel => {
      try {
        const html = rel.htmlFn();
        // Extrair só o <div class="container"> interno (sem html/head)
        const m = html.match(/<div class="container">([\s\S]*?)<\/div>\s*<\/body>/);
        const conteudo = m ? m[1] : html;
        corpoTudo += `\n\n<hr style="margin:48px 0; border:none; border-top:3px solid #F59E0B"/>\n` +
                     `<div style="page-break-before:always"></div>\n` +
                     `<div style="background:#1F2937;color:#fff;padding:14px 24px;font-family:'Segoe UI',sans-serif;margin-bottom:0">` +
                     `<div style="font-size:12px;letter-spacing:.5px;color:#FCD34D">${rel.subgrupoLabel}</div>` +
                     `<div style="font-size:18px;font-weight:600;margin-top:2px">${rel.id} · ${rel.titulo}</div>` +
                     `<div style="font-size:11px;color:#D1D5DB;margin-top:4px;font-family:'Courier New',monospace">${rel.fmt}</div>` +
                     `</div>\n${conteudo}`;
      } catch(e) {
        corpoTudo += `<div style="padding:14px;background:#FECACA;color:#991B1B;border-radius:4px;margin:20px 0">` +
                     `<b>Erro ao gerar ${rel.id}:</b> ${e.message}</div>`;
      }
    });
    const htmlConsolidado = wrapHtmlManta(
      `Pacote Completo de Relatórios · ${qual.nome || qual.codManta || 'Estudo'}`,
      corpoTudo,
      {meta: `${habilitados.length} relatórios consolidados`}
    );
    const blob = new Blob([htmlConsolidado], {type:'text/html'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Manta_${nomeEstudo}_${dataHoje}_PacoteCompleto.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    flash(`📦 Pacote completo baixado · ${habilitados.length} relatórios em 1 HTML`);
  };

  return (<div style={{display:'flex', gap:16, alignItems:'flex-start'}}>
    {/* ═════════ LADO ESQUERDO · tabela compacta ═════════ */}
    <div style={{flex: preview ? '0 0 55%' : 1, minWidth:0, transition:'flex .25s ease'}}>
      {/* Banner explicativo */}
      <div style={{
        marginBottom:12, padding:'10px 14px', background:M.bgAlt,
        border:`1px solid ${M.borderSoft}`, borderRadius:4,
        fontSize:11.5, color:M.muted, lineHeight:1.5,
      }}>
        <b style={{color:M.title}}>10 relatórios em tabela compacta.</b> Clique em <b>👁</b> para ver no painel direito · <b>⬇</b> para baixar no formato original · <b>🌐</b> para HTML universal.
      </div>

      {/* Flash de mensagem */}
      {msgUltimo && (<div style={{
        marginBottom:12, padding:'8px 14px', background:'#D1FAE5', border:`1px solid #10B981`,
        borderRadius:4, fontSize:11.5, color:'#065F46', fontWeight:600,
      }}>✓ {msgUltimo}</div>)}

      {/* Botão "Exportar tudo" no topo */}
      <div style={{
        marginBottom:14, padding:'10px 14px', background:'#1F2937', color:'#fff',
        borderRadius:4, display:'flex', alignItems:'center', gap:10,
      }}>
        <span style={{fontSize:16}}>📸</span>
        <div style={{flex:1, fontSize:11.5, lineHeight:1.4}}>
          <b style={{color:'#FCD34D'}}>Exportar tudo</b> · gera um <b>HTML único</b> com todos os 10 relatórios concatenados
        </div>
        <button onClick={exportarTudoHtml} style={{
          padding:'7px 16px', background:M.accent, color:'#fff', border:'none', borderRadius:3,
          fontFamily:F.mono, fontSize:10.5, fontWeight:700, letterSpacing:.5, cursor:'pointer',
        }}>📦 EXPORTAR TUDO</button>
      </div>

      {/* ═══ TABELA COMPACTA ═══ */}
      <div style={{background:'#fff', border:`1px solid ${M.border}`, borderRadius:4, overflow:'hidden'}}>
        <table style={{width:'100%', borderCollapse:'collapse', fontFamily:F.body}}>
          <thead>
            <tr>
              {['', 'ID', 'Título · Descrição', 'Formato', 'Ações'].map((h, i) => (
                <th key={i} style={{
                  padding:'7px 10px', background:M.title, color:'#fff',
                  fontFamily:F.mono, fontSize:10, fontWeight:700, letterSpacing:.5,
                  textAlign: i === 4 ? 'right' : 'left', textTransform:'uppercase',
                  borderBottom:`2px solid ${M.accent}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* ─── GRUPO A · POR ESTUDO ─── */}
            <tr><td colSpan={5} style={{
              padding:'8px 12px', background:M.bgAlt, color:M.title,
              fontFamily:F.display, fontSize:11.5, fontWeight:700, letterSpacing:.4,
              borderTop:`1px solid ${M.border}`,
            }}>
              📄 A · Por Estudo Individual ({qual.nome || qual.codManta || 'sem estudo carregado'})
            </td></tr>
            {['documentacao', 'apresentacao', 'trabalho'].map(sgKey => {
              const cards = relatorios.filter(r => r.grupo === 'A' && r.subgrupo === sgKey);
              if (cards.length === 0) return null;
              return (<Fragment key={'subA-'+sgKey}>
                <tr><td colSpan={5} style={{
                  padding:'4px 10px 4px 24px', background:'#F9FAFB',
                  fontFamily:F.mono, fontSize:9.5, color:M.muted, letterSpacing:.4,
                  fontWeight:700, borderTop:`1px solid ${M.borderSoft}`,
                }}>
                  {cards[0].subgrupoLabel}
                </td></tr>
                {cards.map(r => (<LinhaRelatorio key={r.id} r={r} onAbrirPreview={()=>abrirPreview(r)} previewAtivo={preview?.id === r.id}/>))}
              </Fragment>);
            })}

            {/* ─── GRUPO B · CONSOLIDADOS ─── */}
            <tr><td colSpan={5} style={{
              padding:'8px 12px', background:M.bgAlt, color:M.title,
              fontFamily:F.display, fontSize:11.5, fontWeight:700, letterSpacing:.4,
              borderTop:`2px solid ${M.border}`,
            }}>
              📈 B · Consolidados do Portfólio ({universoEstudos.length} estudos no universo)
            </td></tr>
            {['comparativos', 'quantitativos', 'conformidade'].map(sgKey => {
              const cards = relatorios.filter(r => r.grupo === 'B' && r.subgrupo === sgKey);
              if (cards.length === 0) return null;
              return (<Fragment key={'subB-'+sgKey}>
                <tr><td colSpan={5} style={{
                  padding:'4px 10px 4px 24px', background:'#F9FAFB',
                  fontFamily:F.mono, fontSize:9.5, color:M.muted, letterSpacing:.4,
                  fontWeight:700, borderTop:`1px solid ${M.borderSoft}`,
                }}>
                  {cards[0].subgrupoLabel}
                </td></tr>
                {cards.map(r => (<LinhaRelatorio key={r.id} r={r} onAbrirPreview={()=>abrirPreview(r)} previewAtivo={preview?.id === r.id}/>))}
              </Fragment>);
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* ═════════ LADO DIREITO · DRAWER DE PREVIEW (consertado) ═════════ */}
    {preview && (<div style={{
      flex: '0 0 45%', minWidth:0,
      maxHeight: 'calc(100vh - 80px)',
      display:'flex', flexDirection:'column',
      background:'#fff', border:`2px solid ${M.accent}`, borderRadius:4,
      boxShadow:'0 4px 16px rgba(0,0,0,.12)',
    }}>
      {/* Header do preview */}
      <div style={{
        padding:'10px 12px', background:M.title, color:'#fff',
        display:'flex', alignItems:'center', gap:8,
        borderBottom:`2px solid ${M.accent}`, borderRadius:'3px 3px 0 0',
        flexShrink:0,
      }}>
        <span style={{fontSize:14}}>👁</span>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:9.5, color:'#FCD34D', letterSpacing:.5, fontFamily:F.mono, fontWeight:700}}>
            {preview.id} · PREVIEW
          </div>
          <div style={{fontSize:12, fontWeight:700, letterSpacing:.3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
            {preview.titulo}
          </div>
        </div>
        <button onClick={()=>setPreview(null)} title="Fechar" style={{
          width:26, height:24, padding:0, background:'transparent',
          border:'1px solid rgba(255,255,255,.3)', borderRadius:3,
          color:'#fff', cursor:'pointer', fontSize:14, lineHeight:1,
        }}>✕</button>
      </div>

      {/* Botoeira de export */}
      <div style={{
        padding:'8px 12px', background:M.bgAlt,
        borderBottom:`1px solid ${M.border}`,
        display:'flex', gap:6, alignItems:'center', flexWrap:'wrap',
        flexShrink:0,
      }}>
        <span style={{fontFamily:F.mono, fontSize:9.5, color:M.muted, letterSpacing:.5, marginRight:4, fontWeight:700}}>⬇</span>
        <button onClick={preview.fnDownload} disabled={preview.disabled} style={{
          padding:'5px 10px', background: preview.disabled ? '#E5E7EB' : M.accent,
          color: preview.disabled ? '#9CA3AF' : '#fff', border:'none', borderRadius:3,
          fontFamily:F.mono, fontSize:10, fontWeight:700, letterSpacing:.3,
          cursor: preview.disabled ? 'not-allowed' : 'pointer',
        }}>⬇ {preview.fmtOriginal.toUpperCase()}</button>
        <button onClick={exportarHtmlPreview} style={{
          padding:'5px 10px', background:'#fff', color:M.title,
          border:`1px solid ${M.border}`, borderRadius:3,
          fontFamily:F.mono, fontSize:10, fontWeight:700, letterSpacing:.3, cursor:'pointer',
        }}>🌐 HTML</button>
        <button onClick={()=>{
          navigator.clipboard.writeText(preview.html).then(()=>{
            flash(`${preview.id} · HTML copiado`);
          });
        }} style={{
          padding:'5px 10px', background:'#fff', color:M.title,
          border:`1px solid ${M.border}`, borderRadius:3,
          fontFamily:F.mono, fontSize:10, fontWeight:700, letterSpacing:.3, cursor:'pointer',
        }}>📋 COPIAR</button>
      </div>

      {/* ─── iframe SEM sandbox · isola estilos naturalmente · funciona sempre ─── */}
      <div style={{
        flex:1, overflow:'hidden', minHeight:400,
        background:'#FAFBFC',
      }}>
        <iframe
          srcDoc={preview.html}
          title={`Preview ${preview.id}`}
          style={{
            width:'100%', height:'100%',
            border:'none', background:'#FAFBFC',
            display:'block',
          }}
        />
      </div>
    </div>)}
  </div>);
});

export { RelatoriosPage };
export default RelatoriosPage;
